"use client";

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Button,
  Container,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Stack,
  Alert,
  Fade,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Description as DocIcon,
  OpenInNew as OpenIcon,
  ArrowBack as BackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as DateIcon,
  Link as LinkIcon,
  Chat as ChatIcon,
  BugReport as BugIcon,
} from "@mui/icons-material";

const ViewDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ticketData = location.state?.ticketData || {};
  const [imageError, setImageError] = useState(false);

  const getStatusColor = (status) => {
    const statusColors = {
      Closed: { color: "error", bg: "#ffebee", text: "#c62828" },
      Resolved: { color: "success", bg: "#e8f5e8", text: "#2e7d32" },
      "In Progress": { color: "primary", bg: "#e3f2fd", text: "#1565c0" },
      Open: { color: "warning", bg: "#fff3e0", text: "#ef6c00" },
      Pending: { color: "info", bg: "#e1f5fe", text: "#0277bd" },
    };
    return statusColors[status] || statusColors["Open"];
  };

  const getIssueTypeIcon = (type) => {
    const icons = {
      "Bug Report": <BugIcon />,
      "Feature Request": <DocIcon />,
      "Technical Support": <ChatIcon />,
      "General Inquiry": <PersonIcon />,
    };
    return icons[type] || <BugIcon />;
  };

  if (!ticketData.ticketId) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              textAlign: "center",
              py: 8,
              px: 4,
              borderRadius: 3,
              background: "var(--bg)",
            }}
          >
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 300 }}>
              No Ticket Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              The ticket you're looking for doesn't exist or has been removed.
            </Typography>
            <Button
              variant="contained"
              startIcon={<BackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Return to Tickets
            </Button>
          </Paper>
        </Fade>
      </Container>
    );
  }

  const statusConfig = getStatusColor(ticketData.status);

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: 8 }}>
      <Fade in timeout={800}>
        <Box>
          {/* Header Section */}
          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 500,
                color: "var(--bg)",
                borderColor: "var(--bg)",
                "&:hover": {
                  backgroundColor: "var(--bg)",
                  color: "white",
                },
              }}
            >
              Back to Tickets
            </Button>

            <Chip
              label={ticketData.status}
              sx={{
                backgroundColor: statusConfig.bg,
                color: statusConfig.text,
                fontWeight: 600,
                fontSize: "0.875rem",
                height: 36,
                borderRadius: 2,
                "& .MuiChip-label": {
                  px: 2,
                },
              }}
            />
          </Box>

          {/* Title Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 3,
              background: "var(--bg)",
              color: "white",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                mb: 1,
                fontWeight: 700,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
              }}
            >
              Support Ticket #{ticketData.ticketId}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
              {ticketData.issueType || "General Support"}
            </Typography>
          </Paper>

          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} lg={8}>
              {/* Issue Details Card */}
              <Card
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Avatar
                      sx={{
                        mr: 2,
                        backgroundColor: "primary.main",
                        width: 48,
                        height: 48,
                      }}
                    >
                      {getIssueTypeIcon(ticketData.issueType)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        Issue Details
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Complete information about this support request
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            backgroundColor: "info.light",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <DateIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            SUBMITTED DATE
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ticketData.date || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            backgroundColor: "success.light",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            CUSTOMER NAME
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ticketData.name || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            backgroundColor: "warning.light",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <EmailIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            EMAIL ADDRESS
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ticketData.email || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    {ticketData.pageUrl && (
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            sx={{
                              backgroundColor: "error.light",
                              width: 32,
                              height: 32,
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 500 }}
                            >
                              PAGE URL
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 500,
                                wordBreak: "break-all",
                                fontSize: "0.875rem",
                              }}
                            >
                              {ticketData.pageUrl}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Description Card */}
              <Card
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                    Issue Description
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      backgroundColor: "grey.50",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.7,
                        color: "text.primary",
                      }}
                    >
                      {ticketData.description || "No description provided"}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>

              {/* Attachment Card */}
              {ticketData.screenshot && (
                <Card
                  elevation={0}
                  sx={{
                    mb: 4,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {ticketData.screenshot.toLowerCase().endsWith(".pdf")
                          ? "PDF Attachment"
                          : ticketData.screenshot
                              .toLowerCase()
                              .endsWith(".doc") ||
                            ticketData.screenshot
                              .toLowerCase()
                              .endsWith(".docx")
                          ? "Document Attachment"
                          : "Screenshot Attachment"}
                      </Typography>

                      <Button
                        variant="contained"
                        startIcon={<OpenIcon />}
                        href={
                          ticketData.screenshot.toLowerCase().endsWith(".pdf")
                            ? ticketData.screenshot
                            : ticketData.screenshot
                                .toLowerCase()
                                .endsWith(".doc") ||
                              ticketData.screenshot
                                .toLowerCase()
                                .endsWith(".docx")
                            ? `https://docs.google.com/viewer?url=${encodeURIComponent(
                                ticketData.screenshot
                              )}&embedded=true`
                            : ticketData.screenshot
                        }
                        target="_blank"
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 500,
                          backgroundColor: "var(--bg)",
                          ":hover": {
                            backgroundColor: "var(--bg)",
                          },
                        }}
                      >
                        Open in New Tab
                      </Button>
                    </Box>

                    <Paper
                      elevation={0}
                      sx={{
                        border: "2px solid",
                        borderColor: "grey.200",
                        borderRadius: 2,
                        overflow: "hidden",
                        backgroundColor: "grey.50",
                      }}
                    >
                      {ticketData.screenshot.toLowerCase().endsWith(".pdf") ? (
                        <Box sx={{ height: "600px" }}>
                          <iframe
                            src={`${ticketData.screenshot}#toolbar=0&navpanes=0`}
                            title="PDF Attachment"
                            width="100%"
                            height="100%"
                            style={{ border: "none" }}
                          />
                        </Box>
                      ) : ticketData.screenshot
                          .toLowerCase()
                          .endsWith(".doc") ||
                        ticketData.screenshot
                          .toLowerCase()
                          .endsWith(".docx") ? (
                        <Box sx={{ height: "600px" }}>
                          <iframe
                            src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                              ticketData.screenshot
                            )}&embedded=true`}
                            title="Document Attachment"
                            width="100%"
                            height="100%"
                            style={{ border: "none" }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          {!imageError ? (
                            <img
                              src={ticketData.screenshot || "/placeholder.svg"}
                              alt="Issue screenshot"
                              onError={() => setImageError(true)}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "500px",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              }}
                            />
                          ) : (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                              Unable to load image attachment
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Right Column - Conversation */}
            <Grid item xs={12} lg={4}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  position: "sticky",
                  top: 24,
                  height: "600px", // Fixed height to match document viewer
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Avatar
                      sx={{
                        mr: 2,
                        backgroundColor: "secondary.main",
                        width: 40,
                        height: 40,
                      }}
                    >
                      <ChatIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Conversation History
                    </Typography>
                  </Box>

                  {ticketData.conversations &&
                  ticketData.conversations.length > 0 ? (
                    <Box
                      sx={{
                        overflowY: "auto",
                        flexGrow: 1,
                        pr: 1,
                        mr: -1,
                        // Custom scrollbar styling
                        "&::-webkit-scrollbar": {
                          width: "8px"
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f1f1",
                          borderRadius: "10px"
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#bbb",
                          borderRadius: "10px",
                          "&:hover": {
                            backgroundColor: "#999"
                          }
                        }
                      }}
                    >
                      <Stack spacing={2}>
                        {ticketData.conversations.map((message, index) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={(theme) => ({
                            p: 2.5,
                            borderRadius: 2,
                            backgroundColor:
                              message.sender === "Support"
                                ? alpha(theme.palette.primary.light, 0.15) // lighter background
                                : alpha(theme.palette.grey[100], 0.7), // user message lighter
                            border: "1px solid",
                            borderColor:
                              message.sender === "Support"
                                ? theme.palette.primary.main
                                : theme.palette.grey[300],
                            position: "relative",
                          })}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color:
                                  message.sender === "Support"
                                    ? "primary.dark"
                                    : "text.primary",
                              }}
                            >
                              {message.sender}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {message.time}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.5,
                              color:
                                message.sender === "Support"
                                  ? "primary.dark"
                                  : "text.primary",
                            }}
                          >
                            {message.message}
                          </Typography>
                        </Paper>
                      ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        textAlign: "center",
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: "grey.300",
                        flexGrow: 1, // Take remaining space
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <ChatIcon
                        sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No conversation history available yet
                      </Typography>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Container>
  );
};

export default ViewDetails;
